'use memo'

import { useHistory, useLocation } from 'react-router-dom'
import {
  mergeOrgRegistrationFiltersIntoSearch,
  orgRegistrationFiltersFromParams,
} from '@sportninja/common/features/organization/registration/utils/filterParams'
import type {
  OrgRegistrationFilters,
  OrgRegistrationTab,
} from '@sportninja/common/features/organization/registration/types'

interface UseOrgRegistrationUrlFiltersResult {
  filters: OrgRegistrationFilters
  setStatus: (status: OrgRegistrationTab) => void
  setSearch: (search: string) => void
  setSport: (
    sport: Pick<OrgRegistrationFilters, 'sportId' | 'genericSportId'>
  ) => void
  setVenue: (venueId: string) => void
  setShowChildOrganizations: (show: boolean) => void
}

/**
 * The Registrations tab's filters, held in the URL so the list can be shared
 * as a link. The URL is the only source of truth: every change writes the
 * query string, and the filters are read back from it.
 *
 * The status tab `push`es, so Back steps between tabs; search, sport, location
 * and the child-organizations switch `replace`, so typing doesn't fill the history with one entry
 * per keystroke.
 */
export const useOrgRegistrationUrlFilters =
  (): UseOrgRegistrationUrlFiltersResult => {
    'use memo'

    const history = useHistory()
    const { pathname, search } = useLocation()
    const filters = orgRegistrationFiltersFromParams(search)

    const write = (next: OrgRegistrationFilters, mode: 'push' | 'replace') => {
      const nextSearch = mergeOrgRegistrationFiltersIntoSearch(search, next)
      if (nextSearch === new URLSearchParams(search).toString()) {
        return
      }
      history[mode]({ pathname, search: nextSearch })
    }

    const setStatus = (status: OrgRegistrationTab) =>
      write({ ...filters, status }, 'push')

    const setSearch = (value: string) =>
      write({ ...filters, search: value }, 'replace')

    const setSport = (
      sport: Pick<OrgRegistrationFilters, 'sportId' | 'genericSportId'>
    ) => write({ ...filters, ...sport }, 'replace')

    const setVenue = (venueId: string) =>
      write({ ...filters, venueId }, 'replace')

    const setShowChildOrganizations = (showChildOrganizations: boolean) =>
      write({ ...filters, showChildOrganizations }, 'replace')

    return {
      filters,
      setStatus,
      setSearch,
      setSport,
      setVenue,
      setShowChildOrganizations,
    }
  }
